import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Modal, Row, Col, Spinner, Badge, Card, CardBody, InputGroup, Input, Button, Nav, NavItem, NavLink, TabContent, TabPane } from 'reactstrap';
import { motion, AnimatePresence } from 'framer-motion';
import { formatRoleName } from '../constants/utils';
import FeatherIcon from 'feather-icons-react';
import { WorkOrdersTable } from './Common';

const ReportedByModal = ({
  isOpen,
  toggle,
  userData,
  loading,
  formatCreatedDate,
  onTicketClick
}) => {
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState('profile');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedStatus, setSelectedStatus] = useState('');

  if (!userData) return null;

  // Animation variants
  const modalVariants = {
    hidden: { 
      opacity: 0, 
      scale: 0.8,
      y: -50
    },
    visible: { 
      opacity: 1, 
      scale: 1,
      y: 0,
      transition: {
        type: "spring",
        stiffness: 300,
        damping: 25
      }
    },
    exit: {
      opacity: 0,
      scale: 0.8,
      y: 50,
      transition: {
        duration: 0.2
      }
    }
  };

  const cardVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { 
      opacity: 1, 
      y: 0,
      transition: { duration: 0.3 }
    }
  };

  // Filter work orders
  const filteredWorkOrders = userData.reportedIssues?.filter(issue => {
    const matchesSearch = searchTerm === '' || 
      issue.ticketNumber?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      issue.asset?.assetName?.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesStatus = selectedStatus === '' || issue.status === selectedStatus;
    
    return matchesSearch && matchesStatus;
  }) || [];

  // Get unique statuses
  const uniqueStatuses = [...new Set(userData.reportedIssues?.map(item => item.status) || [])];



  // Get verification badge
  const getVerificationBadge = (verified) => {
    return (
      <Badge 
        color={verified ? 'success' : 'warning'}
        className="badge-modern d-inline-flex align-items-center"
        style={{
          padding: '6px 12px',
          borderRadius: '20px',
          fontSize: '11px',
          fontWeight: '500'
        }}
      >
        <i className={`bx ${verified ? 'bx-check-shield' : 'bx-shield-x'} me-1`} style={{ fontSize: '12px' }}></i>
        {verified ? t('shared.verified', 'Verified') : t('shared.status_pending_verification', 'Pending Verification')}
      </Badge>
    );
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <Modal
          isOpen={isOpen}
          toggle={toggle}
          size="xl"
          centered
          className="modern-modal"
          backdrop="static"
          style={{ maxHeight: '90vh' }}
        >
          <motion.div
            variants={modalVariants}
            initial="hidden"
            animate="visible"
            exit="exit"
            className="modal-content border-0 shadow-lg"
            style={{ borderRadius: '12px', overflow: 'hidden' }}
          >
            {/* Enhanced Header */}
            <div 
              className="modal-header border-0 text-white position-sticky" 
              style={{ 
                top: 0, 
                zIndex: 10,
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                padding: '1.5rem'
              }}
            >
              <div className="d-flex align-items-center">
                {/* Profile Image */}
                <div className="profile-avatar me-3">
                  {userData.profileUrl ? (
                    <img 
                      src={userData.profileUrl} 
                      alt="Profile" 
                      className="rounded-circle border border-white border-3"
                      style={{ 
                        width: '64px', 
                        height: '64px', 
                        objectFit: 'cover',
                        boxShadow: '0 4px 12px rgba(0,0,0,0.15)'
                      }}
                    />
                  ) : (
                    <div 
                      className="rounded-circle border border-white border-3 d-flex align-items-center justify-content-center bg-white bg-opacity-20"
                      style={{ width: '64px', height: '64px' }}
                    >
                      <i className="bx bx-user text-white fs-2"></i>
                    </div>
                  )}
                  {/* Attendance Status Indicator */}
                  <div 
                    className={`position-absolute bottom-0 end-0 rounded-circle border border-white ${
                      userData.attendanceStatus === 'ONLINE' ? 'bg-success' : 'bg-secondary'
                    }`}
                    style={{ width: '16px', height: '16px' }}
                    title={userData.attendanceStatus === 'ONLINE' ? t('shared.attendance_online', 'Online') : t('shared.attendance_offline', 'Offline')}
                  ></div>
                </div>
                
                <div>
                  <h4 className="modal-title mb-1 text-white">{userData.name}</h4>
                  <div className="d-flex align-items-center gap-2 flex-wrap">
                    <span className="text-white-50 small">
                      <i className="bx bx-envelope me-1"></i>
                      {userData.email || t('shared.no_email_provided', 'No email provided')}
                    </span>
                    <span className="text-white-50 small">•</span>
                    <span className="text-white-50 small">
                      <i className="bx bx-id-card me-1"></i>
                      {formatRoleName(userData.role, t)}
                    </span>
                  </div>
                </div>
              </div>
              <button
                type="button"
                className="btn-close btn-close-white"
                onClick={toggle}
                aria-label={t('shared.close', 'Close')}
              ></button>
            </div>

            {/* Enhanced Navigation */}
            <Nav 
              tabs 
              className="border-bottom-0 px-4 pt-3 bg-light"
            >
              <NavItem>
                <NavLink
                  className={`nav-link-modern ${activeTab === 'profile' ? 'active' : ''}`}
                  onClick={() => setActiveTab('profile')}
                  style={{
                    border: 'none',
                    borderRadius: '8px 8px 0 0',
                    padding: '12px 20px',
                    fontWeight: '500',
                    transition: 'all 0.3s ease',
                    cursor: 'pointer'
                  }}
                >
                  <i className="bx bx-user-circle me-2"></i> 
                  {t('shared.profile_details', 'Profile Details')}
                </NavLink>
              </NavItem>
              <NavItem>
                <NavLink
                  className={`nav-link-modern ${activeTab === 'workOrders' ? 'active' : ''}`}
                  onClick={() => setActiveTab('workOrders')}
                  style={{
                    border: 'none',
                    borderRadius: '8px 8px 0 0',
                    padding: '12px 20px',
                    fontWeight: '500',
                    transition: 'all 0.3s ease',
                    cursor: 'pointer'
                  }}
                >
                  <i className="bx bx-list-ul me-2"></i> 
                  {t('shared.work_orders', 'Work Orders')}
                  {userData.reportedIssues?.length > 0 && (
                    <Badge 
                      color="primary" 
                      pill 
                      className="ms-2"
                      style={{ fontSize: '10px' }}
                    >
                      {userData.reportedIssues.length}
                    </Badge>
                  )}
                </NavLink>
              </NavItem>
            </Nav>

            {/* Modal Body */}
            <div 
              className="modal-body p-0" 
              style={{ 
                maxHeight: 'calc(90vh - 200px)',
                overflow: 'hidden'
              }}
            >
              {loading ? (
                <div className="text-center py-5">
                  <Spinner color="primary" size="lg" />
                  <p className="text-muted mt-3">{t('business.components.reported_by_modal.loading_user_details', 'Loading user details...')}</p>
                </div>
              ) : (
                <TabContent activeTab={activeTab} className="h-100">
                  {/* Profile Tab */}
                  <TabPane tabId="profile" className="h-100">
                    <div className="p-4" style={{ maxHeight: 'calc(90vh - 280px)', overflow: 'auto' }}>
                      {/* Personal Information Card */}
                      <motion.div variants={cardVariants} initial="hidden" animate="visible">
                        <Card className="border-0 shadow-sm mb-4">
                          <CardBody className="p-4">
                            <div className="d-flex align-items-center mb-4">
                              <div className="info-icon me-3">
                                <div className="bg-primary bg-opacity-10 rounded-circle d-flex align-items-center justify-content-center"
                                     style={{ width: '40px', height: '40px' }}>
                                  <i className="bx bx-user text-primary fs-5"></i>
                                </div>
                              </div>
                              <div>
                                <h5 className="mb-1 fw-bold">{t('shared.personal_information', 'Personal Information')}</h5>
                                <p className="text-muted small mb-0">{t('business.components.reported_by_modal.personal_info_subtitle', 'User account and contact details')}</p>
                              </div>
                            </div>

                            <Row className="g-4">
                              <Col md={6}>
                                <div className="info-item">
                                  <div className="d-flex align-items-center mb-2">
                                    <i className="bx bx-user text-muted me-2"></i>
                                    <span className="fw-semibold text-muted small">{t('shared.full_name', 'Full Name')}</span>
                                  </div>
                                  <p className="mb-0 fs-6 text-dark">{userData.name}</p>
                                </div>
                              </Col>
                              <Col md={6}>
                                <div className="info-item">
                                  <div className="d-flex align-items-center mb-2">
                                    <i className="bx bx-envelope text-muted me-2"></i>
                                    <span className="fw-semibold text-muted small">{t('shared.email_address', 'Email Address')}</span>
                                  </div>
                                  <p className="mb-0 fs-6 text-dark">{userData.email || t('shared.not_provided', 'Not provided')}</p>
                                </div>
                              </Col>
                              <Col md={6}>
                                <div className="info-item">
                                  <div className="d-flex align-items-center mb-2">
                                    <i className="bx bx-at text-muted me-2"></i>
                                    <span className="fw-semibold text-muted small">{t('shared.username', 'Username')}</span>
                                  </div>
                                  <p className="mb-0 fs-6 text-dark">{userData.username || t('shared.not_set', 'Not set')}</p>
                                </div>
                              </Col>
                              <Col md={6}>
                                <div className="info-item">
                                  <div className="d-flex align-items-center mb-2">
                                    <i className="bx bx-phone text-muted me-2"></i>
                                    <span className="fw-semibold text-muted small">{t('shared.contact_number', 'Contact Number')}</span>
                                  </div>
                                  <p className="mb-0 fs-6 text-dark">
                                    {userData.phoneCode && userData.contactNumber 
                                      ? `+${userData.phoneCode} ${userData.contactNumber}`
                                      : userData.contactNumber || t('shared.not_provided', 'Not provided')
                                    }
                                  </p>
                                </div>
                              </Col>
                            </Row>
                          </CardBody>
                        </Card>
                      </motion.div>

                      {/* Professional Information Card */}
                      <motion.div 
                        variants={cardVariants} 
                        initial="hidden" 
                        animate="visible"
                        transition={{ delay: 0.1 }}
                      >
                        <Card className="border-0 shadow-sm mb-4">
                          <CardBody className="p-4">
                            <div className="d-flex align-items-center mb-4">
                              <div className="info-icon me-3">
                                <div className="bg-success bg-opacity-10 rounded-circle d-flex align-items-center justify-content-center"
                                     style={{ width: '40px', height: '40px' }}>
                                  <i className="bx bx-briefcase text-success fs-5"></i>
                                </div>
                              </div>
                              <div>
                                <h5 className="mb-1 fw-bold">{t('shared.professional_information', 'Professional Information')}</h5>
                                <p className="text-muted small mb-0">{t('shared.role_and_workplace_details', 'Role and workplace details')}</p>
                              </div>
                            </div>

                            <Row className="g-4">
                              <Col md={6}>
                                <div className="info-item">
                                  <div className="d-flex align-items-center mb-2">
                                    <i className="bx bx-id-card text-muted me-2"></i>
                                    <span className="fw-semibold text-muted small">{t('shared.role', 'Role')}</span>
                                  </div>
                                  <p className="mb-0 fs-6 text-dark">{formatRoleName(userData.role, t) || t('shared.not_specified', 'Not specified')}</p>
                                </div>
                              </Col>
                              <Col md={6}>
                                <div className="info-item">
                                  <div className="d-flex align-items-center mb-2">
                                    <i className="bx bx-shield-check text-muted me-2"></i>
                                    <span className="fw-semibold text-muted small">{t('shared.verification_status', 'Verification Status')}</span>
                                  </div>
                                  <div>{getVerificationBadge(userData.verified)}</div>
                                </div>
                              </Col>
                              <Col md={6}>
                                <div className="info-item">
                                  <div className="d-flex align-items-center mb-2">
                                    <i className="bx bx-radio-circle text-muted me-2"></i>
                                    <span className="fw-semibold text-muted small">{t('shared.attendance_status', 'Attendance Status')}</span>
                                  </div>
                                  <Badge 
                                    color={userData.attendanceStatus === 'ONLINE' ? 'success' : 'secondary'}
                                    className="badge-modern d-inline-flex align-items-center"
                                    style={{
                                      padding: '6px 12px',
                                      borderRadius: '20px',
                                      fontSize: '11px',
                                      fontWeight: '500'
                                    }}
                                  >
                                    <i className={`bx ${userData.attendanceStatus === 'ONLINE' ? 'bx-circle' : 'bx-circle'} me-1`} 
                                       style={{ fontSize: '12px', color: userData.attendanceStatus === 'ONLINE' ? 'green' : 'gray' }}></i>
                                    {userData.attendanceStatus === 'ONLINE' ? t('shared.attendance_online', 'Online') : t('shared.attendance_offline', 'Offline')}
                                  </Badge>
                                </div>
                              </Col>
                            </Row>
                          </CardBody>
                        </Card>
                      </motion.div>

                      {/* Organization Information Card */}
                      <motion.div 
                        variants={cardVariants} 
                        initial="hidden" 
                        animate="visible"
                        transition={{ delay: 0.2 }}
                      >
                        <Card className="border-0 shadow-sm">
                          <CardBody className="p-4">
                            <div className="d-flex align-items-center mb-4">
                              <div className="info-icon me-3">
                                <div className="bg-info bg-opacity-10 rounded-circle d-flex align-items-center justify-content-center"
                                     style={{ width: '40px', height: '40px' }}>
                                  <i className="bx bx-home-heart text-info fs-5"></i>
                                </div>
                              </div>
                              <div>
                                <h5 className="mb-1 fw-bold">{t('shared.organization_information', 'Organization Information')}</h5>
                                <p className="text-muted small mb-0">{t('shared.workplace_and_institution_details', 'Workplace and institution details')}</p>
                              </div>
                            </div>

                            <Row className="g-4">
                              <Col md={6}>
                                <div className="info-item">
                                  <div className="d-flex align-items-center mb-2">
                                    <i className="bx bx-buildings text-muted me-2"></i>
                                    <span className="fw-semibold text-muted small">{t('shared.organization_name', 'Organization Name')}</span>
                                  </div>
                                  <p className="mb-0 fs-6 text-dark">{userData.organization?.name || t('shared.not_specified', 'Not specified')}</p>
                                </div>
                              </Col>
                              <Col md={6}>
                                <div className="info-item">
                                  <div className="d-flex align-items-center mb-2">
                                    <i className="bx bx-phone text-muted me-2"></i>
                                    <span className="fw-semibold text-muted small">{t('shared.organization_contact', 'Organization Contact')}</span>
                                  </div>
                                  <p className="mb-0 fs-6 text-dark">{userData.organization?.contactNumber || t('shared.not_provided', 'Not provided')}</p>
                                </div>
                              </Col>
                            </Row>
                          </CardBody>
                        </Card>
                      </motion.div>
                    </div>
                  </TabPane>

                  {/* Work Orders Tab */}
                  <TabPane tabId="workOrders" className="h-100">
                    <div className="p-4">
                      {/* Search and Filter Section */}
                      {userData.reportedIssues?.length > 0 && (
                        <Card className="border-0 shadow-sm mb-4">
                          <CardBody className="p-3">
                            <div className="d-flex align-items-center mb-3">
                              <i className="bx bx-filter-alt text-primary me-2 fs-5"></i>
                              <h6 className="mb-0 text-muted">{t('shared.search_and_filter', 'Search & Filter')}</h6>
                            </div>
                            
                            <Row className="g-3">
                              <Col md={10}>
                                <InputGroup size="sm">
                                  <span className="input-group-text bg-transparent border-end-0">
                                    <i className="bx bx-search text-muted"></i>
                                  </span>
                                  <Input
                                    type="text"
                                    placeholder={t('shared.search_tickets_assets', 'Search tickets, assets...')}
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                    className="border-start-0 ps-0"
                                  />
                                </InputGroup>
                              </Col>
                              <Col md={2}>
                                <Button
                                  color="outline-secondary"
                                  size="sm"
                                  onClick={() => {
                                    setSearchTerm('');
                                    setSelectedStatus('');
                                  }}
                                  disabled={!searchTerm && !selectedStatus}
                                  className="w-100"
                                >
                                  <i className="bx bx-x"></i>
                                </Button>
                              </Col>
                            </Row>
                          </CardBody>
                        </Card>
                      )}

                      {/* Work Orders Table */}
                      <div style={{ maxHeight: 'calc(90vh - 420px)', overflow: 'auto' }}>
                        {filteredWorkOrders.length > 0 ? (
                          <WorkOrdersTable
                            data={filteredWorkOrders}
                            columns={['ticketNumber', 'createdAt', 'asset', 'status']}
                            onTicketClick={onTicketClick}
                            showRowNumbers={false}
                          />
                        ) : userData.reportedIssues?.length === 0 ? (
                          <div className="text-center py-5">
                            <div className="empty-state-icon d-inline-flex align-items-center justify-content-center rounded-circle mb-3"
                                 style={{ width: '64px', height: '64px', backgroundColor: '#f8f9fa' }}>
                              <i className="bx bx-list-ul text-muted fs-1"></i>
                            </div>
                            <h5 className="text-muted">{t('shared.no_work_orders_found', 'No work orders found')}</h5>
                            <p className="text-muted mb-0">{t('business.components.reported_by_modal.no_reported_orders', "This user hasn't reported any work orders yet")}</p>
                          </div>
                        ) : (
                          <div className="text-center py-5">
                            <div className="empty-state-icon d-inline-flex align-items-center justify-content-center rounded-circle mb-3"
                                 style={{ width: '64px', height: '64px', backgroundColor: '#f8f9fa' }}>
                              <i className="bx bx-search text-muted fs-1"></i>
                            </div>
                            <h5 className="text-muted">{t('shared.no_matches_found', 'No matches found')}</h5>
                            <p className="text-muted mb-0">{t('shared.adjust_filters_search', 'Try adjusting your search terms or filters')}</p>
                          </div>
                        )}
                      </div>
                    </div>
                  </TabPane>
                </TabContent>
              )}
            </div>
          </motion.div>
        </Modal>
      )}
    </AnimatePresence>
  );
};

export default ReportedByModal;